import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  LayoutDashboard, FileText, Users, Tag, BarChart3,
  LogOut, Menu, X, ChevronRight, TrendingUp, Lock, Activity,
  Shield, UserCog, Settings, Users2, Building2, AlertTriangle, MapPin, ChevronsUpDown, Check,
  ListOrdered, Palette, BookOpen
} from "lucide-react";
import phyberLogo from "@/assets/images/phyber-wordmark.png";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/quizzes", label: "Quizzes", icon: FileText, requiredAnyPermission: ["can_view_quizzes", "can_create_quizzes", "can_edit_quizzes", "can_delete_quizzes"] },
  { path: "/sequences", label: "Sequences", icon: ListOrdered, requiredPermission: "can_manage_sequences" },
  { path: "/courses", label: "Courses", icon: BookOpen, requiredPermission: "can_manage_courses" },
  { path: "/groups", label: "Groups", icon: Users, requiredPermission: "can_manage_groups" },
  { path: "/categories", label: "Categories", icon: Tag, requiredPermission: "can_manage_categories" },
  { path: "/reports", label: "Reports", icon: BarChart3, requiredPermission: "can_view_reports" },
  { path: "/analyse", label: "Analyse", icon: TrendingUp, requiredPermission: "can_view_analytics" },
  { path: "/at-risk-students", label: "At-Risk Participants", icon: AlertTriangle, requiredPermission: "can_view_reports" },
  { path: "/management-report-configs", label: "Report Configs", icon: Settings, requiredPermission: "can_manage_report_configs" },
  { path: "/my-quiz-takers", label: "My Quiz Takers", icon: Users2, requiredPermission: "can_manage_quiz_takers" },
  { path: "/organization-settings", label: "Organization", icon: Building2, excludeRole: "superadmin" },
  { path: "/organization-members", label: "Members", icon: Users, excludeRole: "superadmin" },
  { path: "/system-monitoring", label: "System Monitoring", icon: Activity, requiredPermission: "can_view_system_stats" },
  { path: "/locked-accounts", label: "Locked Accounts", icon: Lock, requiredPermission: "can_unlock_accounts" },
  // Superadmin-only pages
  { path: "/organizations", label: "Organizations", icon: Building2, requiredRole: "superadmin" },
  { path: "/portal-theme", label: "Portal Theme", icon: Palette, requiredRole: "superadmin" },
  { path: "/campuses", label: "Campuses", icon: MapPin, requiredRoles: ["superadmin", "org_admin"] },
  { path: "/admin-management", label: "Admin Management", icon: UserCog, requiredRoles: ["superadmin", "org_admin"] },
  { path: "/user-management", label: "User Management", icon: Users, requiredRole: "superadmin" },
  { path: "/audit-logs", label: "Audit Logs", icon: Shield, requiredRole: "superadmin" },
  { path: "/settings", label: "Settings", icon: Settings, requiredRole: "superadmin" },
];

export default function DashboardLayout({ children }) {
  const { user, organization, logout, isOrgAdmin } = useAuth();
  const { selectedOrg, organizations, setSelectedOrg, isSuperadmin, loading: orgLoading } = useOrganizationContext();
  const { campuses, selectedCampus, setSelectedCampus, loading: campusLoading, isMultiCampusAdmin, shouldShowSelector } = useCampusContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [campusOpen, setCampusOpen] = useState(false);

  // Helper to preserve org + campus parameters in navigation
  const getNavPath = (path) => {
    const params = new URLSearchParams();
    if (isSuperadmin && selectedOrg?.id) params.set('org', selectedOrg.id);
    if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) params.set('campus', selectedCampus.id);
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  };

  // Use organization logo if available, otherwise fall back to Phyber logo
  const logoSrc = organization?.logo_url || phyberLogo;
  const brandName = organization?.name || "Phyber";

  const handleLogout = () => {
    logout();
    // Note: logout() already handles redirect to /login with window.location.href
    // This ensures complete state reset and prevents stale UI
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!permission) return true; // No permission required
    if (user?.role === 'superadmin') return true; // Superadmin bypasses all permission checks
    if (!user?.permissions) return false; // User has no permissions object
    return user.permissions[permission] === true;
  };

  // Check if user has required role
  const hasRole = (role) => {
    if (!role) return true; // No role required
    return user?.role === role;
  };

  // Check if user has any of the required roles
  const hasAnyRole = (roles) => {
    if (!roles || roles.length === 0) return true; // No roles required
    return roles.includes(user?.role);
  };

  // Check if user has at least one of the given permissions
  const hasAnyPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    if (user?.role === 'superadmin') return true;
    return permissions.some(p => hasPermission(p));
  };

  // Filter navigation items based on permissions and roles
  const visibleNavItems = NAV_ITEMS.filter(item => {
    // Check role exclusion first
    if (item.excludeRole && hasRole(item.excludeRole)) {
      return false;
    }
    // Check role requirement first (more restrictive)
    if (item.requiredRole && !hasRole(item.requiredRole)) {
      return false;
    }
    // Check multiple roles requirement
    if (item.requiredRoles && !hasAnyRole(item.requiredRoles)) {
      return false;
    }
    // Check single permission requirement
    if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
      return false;
    }
    // Check any-of permissions requirement
    if (item.requiredAnyPermission && !hasAnyPermission(item.requiredAnyPermission)) {
      return false;
    }
    return true;
  });

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-slate-200">
        <img
          src={logoSrc}
          alt={brandName}
          className="h-7 w-auto max-w-full object-contain"
        />
        <p className="text-xs text-slate-500 font-light mt-2">Assessments</p>
      </div>

      {/* Organization Selector (Superadmin Only) */}
      {isSuperadmin && (
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <Label className="text-xs text-slate-600 font-medium mb-2 block">Organization</Label>
          {orgLoading ? (
            <div className="h-9 bg-slate-100 rounded-md animate-pulse" />
          ) : organizations.length === 0 ? (
            <div className="text-xs text-slate-500 py-2">No organizations available</div>
          ) : (
            <Select
              value={selectedOrg?.id || ""}
              onValueChange={(value) => {
                const org = organizations.find(o => o.id === value);
                if (org) {
                  setSelectedOrg(org);
                }
              }}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Select organization..." />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>
                    <div className="flex items-center gap-2">
                      {org.logo_url ? (
                        <img src={org.logo_url} alt={org.name} className="w-4 h-4 object-contain" />
                      ) : (
                        <Building2 className="w-4 h-4 text-slate-400" />
                      )}
                      <span>{org.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!selectedOrg && organizations.length > 0 && (
            <p className="text-xs text-amber-600 mt-1.5">⚠️ Please select an organization</p>
          )}
        </div>
      )}

      {/* Campus Selector — org_admin sees "All Campuses", multi-campus admin does not */}
      {shouldShowSelector && (
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <Label className="text-xs text-slate-600 font-medium mb-2 block">Campus</Label>
          {campusLoading ? (
            <div className="h-9 bg-slate-100 rounded-md animate-pulse" />
          ) : campuses.length === 0 ? (
            <div className="text-xs text-slate-500 py-2">No campuses available</div>
          ) : (
            <Popover open={campusOpen} onOpenChange={setCampusOpen}>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center justify-between gap-2 px-3 h-9 rounded-md border border-slate-200 bg-white text-sm text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="truncate text-slate-700">
                      {selectedCampus?.name || "Select campus..."}
                    </span>
                  </div>
                  <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start" side="bottom">
                <Command>
                  <CommandInput placeholder="Search campus..." className="h-9" />
                  <CommandList>
                    <CommandEmpty>No campus found.</CommandEmpty>
                    <CommandGroup>
                      {campuses.map(campus => (
                        <CommandItem
                          key={campus.id}
                          value={campus.name}
                          onSelect={() => {
                            setSelectedCampus(campus);
                            setCampusOpen(false);
                          }}
                        >
                          <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="ml-2 flex-1">{campus.name}</span>
                          {selectedCampus?.id === campus.id && (
                            <Check className="w-4 h-4 text-indigo-600 shrink-0" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}

      <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto">
        {visibleNavItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={getNavPath(item.path)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group ${
                isActive
                  ? "bg-indigo-50 text-indigo-700"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? "text-indigo-600" : "text-slate-400 group-hover:text-slate-600"}`} />
              {item.label}
              {isActive && <ChevronRight className="w-3 h-3 ml-auto text-indigo-400" />}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">
              {user?.role === 'org_admin' ? 'Org Admin' : user?.role === 'superadmin' ? 'Superadmin' : 'Admin'}
            </p>
            {user?.role === 'admin' && (selectedCampus || user?.campus) && (
              <div className="flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 text-blue-400 flex-shrink-0" />
                <p className="text-xs text-blue-600 font-medium truncate">
                  {selectedCampus?.name || user.campus.name}
                </p>
              </div>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full justify-start text-slate-500 hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 flex-col bg-white border-r border-slate-200 fixed h-full z-30">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl animate-fade-in">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-60">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-1.5 rounded-md hover:bg-slate-100"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
          <img
            src={logoSrc}
            alt={brandName}
            className="h-6 w-auto max-w-[120px] object-contain"
          />
          <span className="text-xs text-slate-500 ml-1">Assessments</span>
          {user?.role === 'admin' && (selectedCampus || user?.campus) && (
            <div className="ml-auto flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1">
              <MapPin className="w-3 h-3 text-blue-500 flex-shrink-0" />
              <span className="text-xs text-blue-700 font-medium">
                {selectedCampus?.name || user.campus.name}
              </span>
            </div>
          )}
        </header>

        {/* Campus Context Banner — single-campus admin only (multi-campus admin uses the selector banner below) */}
        {user?.role === 'admin' && user?.campus && !isMultiCampusAdmin && (
          <div className="sticky top-0 z-10 bg-blue-50 border-b border-blue-200 px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
            <span className="text-xs text-blue-700 font-medium">Campus scope: <strong>{user.campus.name}</strong> — you can only see and manage data from this campus</span>
          </div>
        )}
        {(isOrgAdmin || isMultiCampusAdmin) && selectedCampus && (
          <div className="sticky top-0 z-10 bg-indigo-50 border-b border-indigo-200 px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
            <span className="text-xs text-indigo-700 font-medium">Campus: <strong>{selectedCampus.name}</strong></span>
          </div>
        )}

        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl">
          {children}
        </main>
      </div>
    </div>
  );
}
